<template>
  <q-dialog
    v-model="shown"
    data-test-id="section-settings-form"
  >
    <q-card
      :style=" $q.screen.gt.xs? 'max-height: calc(100vh - 100px); margin-bottom: 50px; max-width: 600px' : undefined"
    >
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Section Settings
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <div class="text-bold q-mb-sm">
          Visiblity
        </div>
        <div class="row q-col-gutter-md items-start">
          <q-toggle
            v-model="section.on"
            class="col-3"
            keep-color
            :color="section.on ? 'positive' : 'grey'"
            unchecked-icon="clear"
            checked-icon="check"
            label="On"
          />
          <q-select
            v-model="section.config.visible_for"
            class="col-9"
            label="Visible For"
            :options="visibilityOptions"
            bottom-slots
            map-options
            emit-value
            use-chips
            clearable
            multiple
          />
          <iso-date-input
            v-model="section.startDate"
            class="col-6"
            label="Start Date"
            clearable
            :rules="[
              () => !section.startDate || !section.endDate || section.startDate <= section.endDate || 'Date cannot postdate end date',
            ]"
          />
          <iso-date-input
            v-model="section.endDate"
            class="col-6"
            label="End Date"
            clearable
            :rules="[
              () => !section.startDate || !section.endDate || section.endDate >= section.startDate || 'Date cannot predate start date',
            ]"
          />
        </div>
      </q-card-section>
      <q-card-section>
        <div class="text-bold q-mb-sm">
          Styling
        </div>
        <div class="row q-col-gutter-md items-start">
          <q-select
            v-model="section.config.widthMode"
            class="col-6"
            label="Width Mode"
            :options="['Centered', 'Full Width']"
          />
          <q-select
            v-model="section.config.marginSides"
            class="col-6"
            label="Margin Sides"
            map-options
            emit-value
            clearable
            :options="styleSizeOptions"
          />
          <q-select
            v-model="section.config.marginTop"
            class="col-6"
            label="Margin Top"
            map-options
            emit-value
            clearable
            :options="styleSizeOptions"
          />
          <q-select
            v-model="section.config.marginBottom"
            class="col-6"
            label="Margin Bottom"
            map-options
            emit-value
            clearable
            :options="styleSizeOptions"
          />
          <color-selection
            :color-name="section.config.backgroundColor"
            :custom-color-hex="section.config.backgroundColorHex"
            :label="'Background Color'"
            class="full-width"
            @update:model-value="handleBackgroundColorInput"
          />

          <template v-if="!section.config.backgroundColor || section.config.backgroundColor === 'custom'">
            <image-uploader
              v-model="section.config.backgroundImage"
              class="col-6"
              min-height="190px"
              :image-url="section.backgroundImageUrl"
              :model="section"
            />
            <div class="col-6 column justify-between q-gutter-sm">
              <q-select
                v-model="section.config.backgroundImageFit"
                :options="IMAGE_TRANSFORM_OPTIONS.ALL"
                :option-label="type => $_.startCase(type)"
                map-options
                emit-value
                label="Image Fit"
                class="col"
              />

              <q-select
                v-model="section.config.backgroundImagePosition"
                :options="IMAGE_POSITION_OPTIONS.ALL"
                :option-label="type => $_.startCase(type)"
                map-options
                emit-value
                label="Image Position"
                class="col"
              />
              <div class="row col">
                <q-select
                  v-model="section.config.backgroundRepeat"
                  :options="backgroundRepeatOptions"
                  map-options
                  emit-value
                  label="Repeat"
                  class="col q-pr-sm"
                />
                <q-select
                  v-model="section.config.backgroundAttachment"
                  :options="backgroundAttachmentOptions"
                  map-options
                  emit-value
                  label="Attachment"
                  class="col"
                />
              </div>
            </div>
          </template>
        </div>
      </q-card-section>
      <q-card-section v-if="sectionSupportHeadingSetting">
        <div class="text-bold q-mb-sm">
          Heading
        </div>
        <div class="row q-col-gutter-md items-start">
          <q-select
            v-model="section.config.headingFontSize"
            class="col-6"
            label="Font Size"
            :options="fontSizeOptions"
            map-options
            emit-value
            clearable
          />
          <q-select
            v-model="section.config.headingAlign"
            class="col-6"
            label="Text Align"
            :options="TEXT_ALIGN_OPTIONS.ALL"
            :option-label="type => $_.startCase(type)"
            map-options
            emit-value
            clearable
          />
          <color-selection
            :color-name="section.config.headingColor"
            :custom-color-hex="section.config.headingColorHex"
            :label="'Color'"
            class="full-width"
            @update:model-value="handleHeadingColorInput"
          />
          <q-select
            v-model="section.config.headingType"
            class="col-6"
            label="Style"
            :options="HEADING_TYPES.ALL"
            map-options
            emit-value
          />
          <template v-if="$_.includes(HEADING_TYPES.IMAGES, section.config.headingType)">
            <image-uploader
              v-if="section.config.headingType === HEADING_TYPES.CUSTOM"
              v-model="section.config.headingBackgroundImage"
              class="col-2"
              width="50px"
              height="50px"
              :image-url="section.headingBackgroundImageUrl"
              :model="section"
            />
            <q-checkbox
              v-model="section.config.headingBackgroundImageRepeat"
              :class="section.config.headingType === HEADING_TYPES.CUSTOM ? 'col-3' : 'col-6'"
              label="Repeat Background"
              bottom-slots
            />
          </template>
        </div>
      </q-card-section>
      <q-card-section v-if="$_.includes(SECTION_TYPES.WIDGETS, section.type)">
        <div class="text-bold q-mb-sm">
          Widget
        </div>
        <div class="row q-gutter-md items-start">
          <q-checkbox
            v-model="section.config.square"
            class="col-3"
            label="Square"
            bottom-slots
          >
            <q-tooltip>Remove box shadow from the widget</q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="section.config.flat"
            class="col-3"
            label="Flat"
            bottom-slots
          >
            <q-tooltip>Remove box shadow from the widget</q-tooltip>
          </q-checkbox>
        </div>
      </q-card-section>
      <q-card-section>
        <div class="text-bold q-mb-sm">
          Advanced Styling
        </div>
        <div class="row q-col-gutter-md items-start">
          <q-input
            v-model="section.config.class"
            class="col-6"
            label="Classes"
            clearable
          />
          <q-input
            v-model="section.config.style"
            class="col-6"
            label="Styles"
            clearable
          />
        </div>
        <div class="row q-col-gutter-md q-mt-sm">
          <q-input
            v-model="section.styles"
            type="textarea"
            class="col-12"
            label="Custom CSS"
          />
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { IMAGE_POSITION_OPTIONS, IMAGE_TRANSFORM_OPTIONS, TEXT_ALIGN_OPTIONS } from 'src/models/carousel/Carousel'
import { IsoDate } from 'src/models/model'
import Section, { backgroundAttachmentOptions, backgroundRepeatOptions, fontSizeOptions, HEADING_TYPES, SECTION_TYPES, styleSizeOptions, VISIBILITY_OPTIONS } from 'src/models/page/Section'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import IsoDateInput from 'src/modules/admin/components/IsoDateInput'

export default {
  name: 'SectionSettingsForm',
  components: {
    IsoDateInput,
    ImageUploader,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  data () {
    return {
      shown: false
    }
  },
  computed: {

    sectionSupportHeadingSetting () {
      return _.includes(SECTION_TYPES.WITH_HEADING, this.section.type)
    },
    visibilityOptions () {
      if (this.section.type === SECTION_TYPES.FOOTER) {
        return this.$mh.asOptions(VISIBILITY_OPTIONS.WEB_ONLY)
      }
      return this.$mh.asOptions(VISIBILITY_OPTIONS.ALL)
    }
  },
  created () {
    _.extend(this, {
      IsoDate,
      styleSizeOptions,
      fontSizeOptions,
      HEADING_TYPES,
      SECTION_TYPES,
      backgroundRepeatOptions,
      backgroundAttachmentOptions,
      IMAGE_TRANSFORM_OPTIONS,
      IMAGE_POSITION_OPTIONS,
      TEXT_ALIGN_OPTIONS,
      VISIBILITY_OPTIONS
    })
  },
  methods: {
    show () {
      this.shown = true
    },
    handleBackgroundColorInput (colorName, customColorHex) {
      this.section.config.backgroundColor = colorName
      this.section.config.backgroundColorHex = customColorHex
    },
    handleHeadingColorInput (colorName, customColorHex) {
      this.section.config.headingColor = colorName
      this.section.config.headingColorHex = customColorHex
    }
  }
}

</script>
